import { FC } from 'react';

import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Combobox } from '@headlessui/react';

import { isNotNullOrUndefined } from 'shared-lib';
import { classNames } from 'shared-ui';

import { identityUserSearchBoxQueryDef } from './IdentityUserSearchBox';

import { IdentityUserSearchBoxQuery } from './__generated__/IdentityUserSearchBoxQuery.graphql';
import { IdentityAccountTypes } from './__generated__/IdentityUserListContainer_list.graphql';

export type IdentityUserSearchResult = {
  readonly accountTypes: ReadonlyArray<IdentityAccountTypes>;
  readonly code: number;
  readonly createdAt: string;
  readonly email: string;
  readonly firstName: string | null | undefined;
  readonly id: string;
  readonly identityUserId: string;
  readonly lastLoggedIn: string | null | undefined;
  readonly lastName: string | null | undefined;
  readonly updatedAt: string;
};

type Props = {
  queryRef: PreloadedQuery<IdentityUserSearchBoxQuery>;
};

export const IdentityUserSearchBoxOptions: FC<Props> = ({ queryRef }) => {
  const data = usePreloadedQuery(identityUserSearchBoxQueryDef, queryRef);

  const { list } = data;

  const items =
    list?.edges?.map(e => e.node).filter(isNotNullOrUndefined) || [];

  if (items.length === 0) {
    return <div></div>;
  }

  return (
    <Combobox.Options className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
      {items.map((result: IdentityUserSearchResult) => (
        <Combobox.Option
          aria-label={`${result.firstName} ${result.lastName} (${result.email})`}
          key={result.identityUserId}
          value={result}
          className={({ active }) =>
            classNames(
              'relative cursor-default select-none py-2 pl-3 pr-9',
              active ? 'bg-info-600 text-white' : 'text-gray-900',
            )
          }
        >
          {({ active, selected }) => (
            <>
              <div className='flex'>
                <span
                  className={classNames(
                    'truncate',
                    selected ? 'font-semibold' : '',
                  )}
                >
                  {`${result.firstName} ${result.lastName} (${result.email})`}
                </span>
              </div>

              {selected && (
                <span
                  className={classNames(
                    'absolute inset-y-0 right-0 flex items-center pr-4',
                    active ? 'bg-info-600 text-white' : 'text-info-600',
                  )}
                >
                  <CheckIcon className='h-5 w-5' aria-hidden='true' />
                </span>
              )}
            </>
          )}
        </Combobox.Option>
      ))}
    </Combobox.Options>
  );
};
